import { createSelector } from 'reselect';
import { userEffectiveAgencyIdSelector } from 'src/store/user/user.selectors';
import { buildJurisdictionNameLookup } from 'src/utils/jurisdiction-utils';

// Agency data selectors
const defaultAgencies = { list: [], agencyMap: {}, loading: false, error: '' };
export const userAgenciesSelector = (state) => state.userInfo.agencies || defaultAgencies;

/**
 * Flat lookup map of jurisdiction IDs to names, derived from agency data.
 * Shape: { [jurisdictionId]: jurisdictionName }
 */
const defaultJurisdictionNameMap = {};
export const jurisdictionNameMapSelector = (state) => (
    state.userInfo.jurisdictionNameMap || defaultJurisdictionNameMap
);

/**
 * Memoized `(jurisdictionId) => name` lookup over `jurisdictionNameMap`,
 * case-insensitive, falling back to the id when no name is known.
 */
export const jurisdictionNameLookupSelector = createSelector(
    jurisdictionNameMapSelector,
    buildJurisdictionNameLookup,
);

// Backward-compatible alias
export const userCitiesSelector = jurisdictionNameMapSelector;

/**
 * Selector that returns the agency map keyed by agencies_id.
 * Shape: { [agencyId]: { agencies_id, agencies_short_name, agencies_long_name, agency_type, jurisdictions: [] } }
 */
export const userAgencyMapSelector = (state) => state.userInfo.agencies?.agencyMap || {};

/**
 * Selector to get the currently selected agency name.
 */
export const selectedAgencyNameSelector = (state) => {
    const agencyMap = userAgencyMapSelector(state);
    const agencyId = userEffectiveAgencyIdSelector(state);
    if (!agencyId) return null;
    return agencyMap[agencyId]?.agencies_long_name || null;
};

/**
 * Resolve any agency ID to its display name.
 * Usage: agencyDisplayNameSelector(state, 'vta') → 'VTA Santa Clara'
 */
export const agencyDisplayNameSelector = (state, agencyId) => {
    if (!agencyId) return null;
    const agencyMap = userAgencyMapSelector(state);
    return agencyMap[agencyId]?.agencies_long_name
        || agencyMap[agencyId]?.agencies_short_name
        || null;
};

/**
 * Selector that returns the agency_type ('transit', 'emergency', 'snow')
 * for the currently selected agency.
 */
export const selectedAgencyTypeSelector = (state) => {
    const agencyMap = userAgencyMapSelector(state);
    const agencyId = userEffectiveAgencyIdSelector(state);
    if (!agencyId) return null;
    return agencyMap[agencyId]?.agency_type || null;
};

/**
 * Selector that derives the jurisdictions (cities) for the currently
 * selected agency from the user agency map.
 *
 * Shape: { list: Array<{ id, name }>, loading: boolean }
 */
export const selectedAgencyJurisdictionsSelector = (state) => {
    const loading = state.userInfo.agencies?.loading || false;
    const agencyMap = userAgencyMapSelector(state);
    const agencyId = userEffectiveAgencyIdSelector(state);
    if (!agencyId) {
        return { list: [], loading };
    }
    const agency = agencyMap[agencyId];
    const jurisdictions = agency?.jurisdictions || [];
    return {
        list: jurisdictions.map(city => ({ id: city.id, name: city.name })),
        loading,
    };
};

/**
 * Selector that returns jurisdictions across ALL of the user's agencies,
 * each tagged with the parent agency name as a `group` field.
 *
 * Used by traffic viewers who may have jurisdictions spanning multiple agencies.
 *
 * Shape: { list: Array<{ id, name, agencyId, group }>, loading: boolean }
 */
export const allAgencyJurisdictionsSelector = (state) => {
    const loading = state.userInfo.agencies?.loading || false;
    const { list: agencies } = userAgenciesSelector(state);
    if (!agencies || agencies.length === 0) {
        return { list: [], loading };
    }
    const jurisdictions = agencies.flatMap(agency => {
        const agencyName = agency.agencies_long_name || agency.agencies_short_name || 'Unknown Agency';
        return (agency.jurisdictions || []).map(city => ({
            id: city.id,
            name: city.name,
            agencyId: agency.agencies_id,
            group: agencyName,
        }));
    });
    return { list: jurisdictions, loading };
};

/**
 * All cities from GET /cities (lazy-loaded for admin Requests page).
 * Shape: { list: Array, loading: boolean, error: string }
 */
const defaultAllCities = { list: [], loading: false, error: '' };
export const allCitiesSelector = (state) => state.userInfo.allCities || defaultAllCities;

/**
 * Selected jurisdiction ID for traffic viewer users.
 */
export const selectedJurisdictionSelector = (state) => state.userInfo.selectedJurisdiction;
